import React, { useEffect, useState, useCallback } from "react";
import { Eye, FileSpreadsheet, FileText, ChevronLeft, ChevronRight, Download } from "lucide-react";
import axios from "axios";
import * as XLSX from "xlsx";
import { fetchAttendanceReportHistoryAPI, getReportFileURL } from "../../actioncreators/actioncreators";

const PAGE_SIZE = 10;

const FILE_TYPE = {
  originalView: "original",
  generatedView: "generated",
  excelDownload: "generated-download",
  pdfView: "pdf",
  pdfDownload: "pdf-download",
};

// ---------------------------------------------------------------------------
// Helpers (module-level, no stale-closure risk)
// ---------------------------------------------------------------------------

// Decodes an error from an axios error regardless of responseType used.
// Handles Blob (responseType:"blob") and ArrayBuffer (responseType:"arraybuffer").
async function extractErrorMessage(err) {
  const data = err?.response?.data;
  if (!data) return err?.message || "An error occurred.";

  try {
    let text;
    if (data instanceof Blob) {
      text = await data.text();
    } else if (data instanceof ArrayBuffer) {
      text = new TextDecoder().decode(data);
    } else if (typeof data === "string") {
      text = data;
    } else {
      // Already a parsed object (e.g. when responseType is not set)
      return data.message || "An error occurred.";
    }
    const json = JSON.parse(text);
    return json.message || text || "An error occurred.";
  } catch {
    return err?.message || "An error occurred.";
  }
}

// Opens a SheetJS-rendered Excel preview in a new browser tab.
// All sheets are rendered; the user can switch between them via tab buttons.
function openExcelPreview(arrayBuffer, fileName) {
  let workbook;
  try {
    workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: "array" });
  } catch {
    alert("Could not parse the Excel file. It may be corrupted.");
    return;
  }

  // Build one hidden <div> per sheet, each containing its HTML table.
  const sheetDivs = workbook.SheetNames.map((name, idx) => {
    const sheet = workbook.Sheets[name];
    const tableHtml = XLSX.utils.sheet_to_html(sheet, { id: `pt-${idx}`, editable: false });
    return `<div class="sheet-panel" id="panel-${idx}" style="display:${idx === 0 ? "block" : "none"}">${tableHtml}</div>`;
  }).join("\n");

  // One tab button per sheet.
  const tabButtons = workbook.SheetNames.map((name, idx) =>
    `<button class="tab-btn${idx === 0 ? " active" : ""}" onclick="switchTab(${idx})">${escapeHtml(name)}</button>`
  ).join("\n");

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>${escapeHtml(fileName)}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:12px;background:#f4f5f7}
header{position:sticky;top:0;z-index:10;background:#fff;border-bottom:1px solid #ddd;
  padding:8px 16px;display:flex;align-items:center;gap:10px}
header span{color:#FF8800;font-weight:bold;font-size:14px;flex:1;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.tab-bar{position:sticky;top:41px;z-index:9;background:#f4f5f7;
  border-bottom:2px solid #ddd;display:flex;flex-wrap:wrap;gap:2px;padding:6px 12px 0}
.tab-btn{padding:5px 14px;border:1px solid #ccc;border-bottom:none;border-radius:4px 4px 0 0;
  background:#e9ecef;cursor:pointer;font-size:12px;color:#444;white-space:nowrap}
.tab-btn.active{background:#fff;border-bottom:2px solid #fff;color:#FF8800;
  font-weight:bold;margin-bottom:-2px}
.tab-btn:hover:not(.active){background:#fff3e0;color:#FF8800}
.wrap{overflow:auto;padding:16px}
.sheet-panel table{border-collapse:collapse;background:#fff;white-space:nowrap;min-width:100%}
.sheet-panel table th,.sheet-panel table td{border:1px solid #ccc;padding:4px 8px;text-align:left}
.sheet-panel table tr:first-child{background:#FFF3E0;font-weight:bold;position:sticky;top:0}
.sheet-panel table tr:nth-child(even){background:#fafafa}
.sheet-panel table tr:hover{background:#fff8e1}
</style>
</head>
<body>
<header>
  <span>arthaTRACK — ${escapeHtml(fileName)}</span>
  <small>${workbook.SheetNames.length} sheet${workbook.SheetNames.length !== 1 ? "s" : ""}</small>
</header>
<div class="tab-bar">${tabButtons}</div>
<div class="wrap">${sheetDivs}</div>
<script>
function switchTab(idx){
  document.querySelectorAll(".sheet-panel").forEach(function(el,i){
    el.style.display = i===idx ? "block" : "none";
  });
  document.querySelectorAll(".tab-btn").forEach(function(btn,i){
    btn.classList.toggle("active", i===idx);
  });
}
</script>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  if (!win) alert("Pop-up blocked. Please allow pop-ups for this site and try again.");
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function excelNameToPDF(name) {
  for (const ext of [".xlsx", ".xls"]) {
    if (name.endsWith(ext)) return name.slice(0, -ext.length) + ".pdf";
  }
  return name + ".pdf";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function AttendanceReportHistory({ embedded = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState({});

  const markBusy = useCallback((key, val) =>
    setBusy((prev) => ({ ...prev, [key]: val })), []);

  // ---- data fetch ----

  const fetchPage = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAttendanceReportHistoryAPI(p, PAGE_SIZE);
      const body = res.data;
      if (body.businessStatusCode === 2) {
        const rows = body.data || [];
        const total = body.total || 0;
        // Compute totalPages client-side from total+limit so we never rely on
        // a stale or zero value echoed back from the server.
        const serverPages = body.totalPages;
        const computedPages = total > 0 ? Math.ceil(total / PAGE_SIZE) : 1;
        const resolvedPages = (serverPages && serverPages > 0) ? serverPages : computedPages;
        setRows(rows);
        setPage(p);
        setTotalPages(resolvedPages);
        setTotal(total);
      } else {
        setError("Failed to load reports.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPage(1); }, [fetchPage]);

  // ---- file actions ----

  // Fetch as arraybuffer, parse with SheetJS, render HTML table in new tab.
  const viewExcel = useCallback(async (reportId, fileType, fileName, busyKey) => {
    markBusy(busyKey, true);
    try {
      const url = getReportFileURL(reportId, fileType);
      const res = await axios.get(url, {
        withCredentials: true,
        responseType: "arraybuffer",
      });
      openExcelPreview(res.data, fileName);
    } catch (err) {
      alert(await extractErrorMessage(err));
    } finally {
      markBusy(busyKey, false);
    }
  }, [markBusy]);

  // Fetch PDF blob (backend converts Excel→PDF via LibreOffice), open in new tab.
  const viewPdf = useCallback(async (reportId, busyKey) => {
    markBusy(busyKey, true);
    try {
      const url = getReportFileURL(reportId, FILE_TYPE.pdfView);
      const res = await axios.get(url, {
        withCredentials: true,
        responseType: "blob",
      });
      // Verify the server actually returned a PDF (not an error JSON as blob)
      const contentType = res.headers["content-type"] || "";
      if (!contentType.includes("pdf")) {
        const text = await res.data.text();
        try { alert(JSON.parse(text).message || "PDF generation failed."); }
        catch { alert("PDF generation failed."); }
        return;
      }
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const win = window.open(blobUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      if (!win) alert("Pop-up blocked. Please allow pop-ups for this site and try again.");
    } catch (err) {
      alert(await extractErrorMessage(err));
    } finally {
      markBusy(busyKey, false);
    }
  }, [markBusy]);

  // Trigger browser Save-As dialog.
  const forceDownload = useCallback(async (reportId, fileType, fileName, busyKey) => {
    markBusy(busyKey, true);
    try {
      const url = getReportFileURL(reportId, fileType);
      const res = await axios.get(url, {
        withCredentials: true,
        responseType: "blob",
      });
      // If error came back as JSON blob, surface the message instead of saving garbage.
      const contentType = res.headers["content-type"] || "";
      if (contentType.includes("application/json")) {
        const text = await res.data.text();
        try { alert(JSON.parse(text).message || "Download failed."); }
        catch { alert("Download failed."); }
        return;
      }
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([res.data], { type: contentType }));
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      alert(await extractErrorMessage(err));
    } finally {
      markBusy(busyKey, false);
    }
  }, [markBusy]);

  // ---- render helpers ----

  const srNo = (i) => (page - 1) * PAGE_SIZE + i + 1;

  const Spinner = () => (
    <span
      className="spinner-border spinner-border-sm"
      role="status"
      aria-hidden="true"
      style={{ width: 11, height: 11, borderWidth: 2 }}
    />
  );

  const FileBtn = ({ label, busyKey, onClick }) => (
    <button
      className="btn btn-sm btn-link p-0 d-flex align-items-center gap-1"
      style={{ color: "#FF8800", textDecoration: "none" }}
      onClick={onClick}
      disabled={!!busy[busyKey]}
      title={label}
    >
      {busy[busyKey] ? <Spinner /> : <Eye size={13} />}
      <span className="text-truncate" style={{ maxWidth: 160 }}>
        {busy[busyKey] ? "Loading…" : label}
      </span>
    </button>
  );

  const ActionBtn = ({ icon, label, color, busyKey, onClick, style = {} }) => (
    <button
      className="btn btn-sm d-inline-flex align-items-center gap-1"
      style={{
        backgroundColor: busy[busyKey] ? "#999" : color,
        color: "#fff", borderRadius: 6, fontSize: 12, ...style,
      }}
      onClick={onClick}
      disabled={!!busy[busyKey]}
    >
      {busy[busyKey] ? <Spinner /> : icon}
      {label}
    </button>
  );

  // ---- main render ----

  return (
    <div className={embedded ? "" : "p-4"}>
      {!embedded && (
        <h5 className="fw-bold mb-1" style={{ color: "#FF8800" }}>
          arthaTRACK Attendance Reports
        </h5>
      )}
      <p className={`text-muted ${embedded ? "mb-2" : "mb-4"}`} style={{ fontSize: 13 }}>
        <strong>Attendance Report History</strong> —{" "}
        <strong>{total}</strong> record{total !== 1 ? "s" : ""}
      </p>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
          <div className="spinner-border" style={{ color: "#FF8800" }} role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger d-flex align-items-center gap-2">
          <span>{error}</span>
          <button className="btn btn-sm btn-outline-danger ms-auto" onClick={() => fetchPage(page)}>
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <FileSpreadsheet size={48} className="mb-3 opacity-25" />
          <p>No reports generated yet.</p>
        </div>
      ) : (
        <>
          <div className="card shadow-sm" style={{ borderRadius: 10, overflow: "hidden" }}>
            <div className="table-responsive">
              <table className="table table-hover mb-0 align-middle" style={{ fontSize: 13 }}>
                <thead style={{ backgroundColor: "#FFF3E0" }}>
                  <tr>
                    <th className="px-3 py-3 text-center" style={{ width: 60 }}>Sr. No.</th>
                    <th className="px-3 py-3">Original File</th>
                    <th className="px-3 py-3">Processed File (arthaTRACK)</th>
                    <th className="px-3 py-3 text-center">Excel Download</th>
                    <th className="px-3 py-3 text-center">PDF</th>
                    <th className="px-3 py-3">Generated At</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const origKey   = `${row.id}-orig`;
                    const genKey    = `${row.id}-gen`;
                    const xlDlKey   = `${row.id}-xl-dl`;
                    const pdfVKey   = `${row.id}-pdf-v`;
                    const pdfDlKey  = `${row.id}-pdf-dl`;
                    const pdfBusy   = busy[pdfVKey] || busy[pdfDlKey];

                    return (
                      <tr key={row.id} style={{ backgroundColor: i % 2 === 0 ? "#fff" : "#fafafa" }}>

                        <td className="px-3 text-center fw-semibold text-muted">{srNo(i)}</td>

                        {/* Original file — SheetJS inline preview */}
                        <td className="px-3">
                          <FileBtn
                            label={row.originalFileName}
                            busyKey={origKey}
                            onClick={() => viewExcel(row.id, FILE_TYPE.originalView, row.originalFileName, origKey)}
                          />
                        </td>

                        {/* Processed file — SheetJS inline preview */}
                        <td className="px-3">
                          <FileBtn
                            label={row.generatedFileName}
                            busyKey={genKey}
                            onClick={() => viewExcel(row.id, FILE_TYPE.generatedView, row.generatedFileName, genKey)}
                          />
                        </td>

                        {/* Excel force-download */}
                        <td className="px-3 text-center">
                          <ActionBtn
                            icon={<FileSpreadsheet size={13} />}
                            label={busy[xlDlKey] ? "…" : "Excel"}
                            color="#217346"
                            busyKey={xlDlKey}
                            onClick={() => forceDownload(row.id, FILE_TYPE.excelDownload, row.generatedFileName, xlDlKey)}
                          />
                        </td>

                        {/* PDF: view + download */}
                        <td className="px-3 text-center">
                          <div className="d-inline-flex gap-1">
                            <ActionBtn
                              icon={<FileText size={13} />}
                              label={busy[pdfVKey] ? "Generating…" : "PDF"}
                              color="#e53935"
                              busyKey={pdfVKey}
                              onClick={() => !pdfBusy && viewPdf(row.id, pdfVKey)}
                            />
                            <button
                              className="btn btn-sm d-inline-flex align-items-center"
                              style={{
                                backgroundColor: pdfBusy ? "#999" : "#b71c1c",
                                color: "#fff", borderRadius: 6, fontSize: 12, padding: "3px 7px",
                              }}
                              onClick={() => !pdfBusy && forceDownload(row.id, FILE_TYPE.pdfDownload, excelNameToPDF(row.generatedFileName), pdfDlKey)}
                              disabled={!!pdfBusy}
                              title="Download PDF"
                            >
                              {busy[pdfDlKey] ? <Spinner /> : <Download size={12} />}
                            </button>
                          </div>
                        </td>

                        <td className="px-3 text-muted">{row.createdAt}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="text-muted" style={{ fontSize: 13 }}>
              Showing {total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} &nbsp;·&nbsp; Page {page} of {totalPages}
            </span>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                disabled={page <= 1}
                onClick={() => fetchPage(page - 1)}
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <button
                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                disabled={page >= totalPages}
                onClick={() => fetchPage(page + 1)}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AttendanceReportHistory;
